import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import RelatorioModuloElasticidadePrint from "./RelatorioModuloElasticidadePrint";
import { auth, db, storage } from "../Config/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useHistory } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";

const estadoInicial = {
  codigo: "",
  dataRelatorio: new Date().toISOString().split("T")[0],
  dataEnsaio: new Date().toISOString().split("T")[0],
  cliente: "",
  clienteId: "",
  obra: "",
  obraId: "",
  dataMoldagem: "",
  notaFiscalRomaneio: "",
  agendaRupturaId: "",
  caminhaoId: "",
  concreteira: "",
  localAplicacao: "",
  fckProjeto: "",
  abatimento: "",
  tipoConcreto: "",
  tipoCura: "Água",
  massaCp1: "",
  alturaCp1: "",
  diametroCp1: "",
  massaCp2: "",
  alturaCp2: "",
  diametroCp2: "",
  moduloEciCp1: "",
  moduloEciCp2: "",
  moduloDinamicoCp1: "",
  moduloDinamicoCp2: "",
  certificadoAparelho: null,
  laudosEnsaio: [],
};

function formatarDataBR(dataISO) {
  if (!dataISO) return "-";
  const [ano, mes, dia] = String(dataISO).split("-");
  if (!ano || !mes || !dia) return dataISO;
  return `${dia}/${mes}/${ano}`;
}

function normalizarTexto(valor) {
  return String(valor || "").toLowerCase();
}

function numeroDecimal(valor) {
  if (valor === "" || valor === null || valor === undefined) return null;
  const numero = Number(String(valor).replace(",", "."));
  return Number.isFinite(numero) ? numero : null;
}

function formatarDecimalDuasCasas(valor) {
  const numero = numeroDecimal(valor);
  return numero === null ? valor : numero.toFixed(2).replace(".", ",");
}

function eciInvalido(eci, dinamico) {
  const valorEci = numeroDecimal(eci);
  const valorDinamico = numeroDecimal(dinamico);
  return valorEci !== null && valorDinamico !== null && valorEci >= valorDinamico;
}

function eciInvalidoAoDigitar(eci, dinamico) {
  const numeroCompleto = (valor) => /^\d+[,.]\d{2}$/.test(String(valor || "").trim());
  return numeroCompleto(eci) && numeroCompleto(dinamico) && eciInvalido(eci, dinamico);
}

function calcularProximoCodigo(lista) {
  const ano = new Date().getFullYear();
  const numeros = lista
    .map((item) => String(item.codigo || "").match(/CE-RE-MED-(\d+)\/\d{4}/))
    .filter(Boolean)
    .map((match) => Number(match[1]))
    .filter(Number.isFinite);

  const proximo = numeros.length ? Math.max(...numeros) + 1 : lista.length + 1;
  return `CE-RE-MED-${String(proximo).padStart(3, "0")}/${ano}`;
}

function numeroCodigoModulo(codigo) {
  const match = String(codigo || "").match(/CE-RE-MED-(\d+)\/(\d{4})/);
  if (!match) return 0;
  return Number(`${match[2]}${match[1].padStart(6, "0")}`);
}

function formatarTipoLancamento(tipoLancamento) {
  if (tipoLancamento === "bombeado") return "Bombeado";
  if (tipoLancamento === "convencional") return "Convencional";
  if (tipoLancamento === "nao_informado") return "N/I";
  return tipoLancamento || "";
}

function concatenarLocalAplicacao(observacoes, pecaConcretada) {
  const obs = String(observacoes || "").trim();
  const peca = String(pecaConcretada || "").trim();
  return [obs, peca].filter(Boolean).join(" - ");
}

function diferencaDias(dataInicial, dataFinal) {
  if (!dataInicial || !dataFinal) return "";

  const inicio = new Date(`${dataInicial}T00:00:00`);
  const fim = new Date(`${dataFinal}T00:00:00`);
  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime())) return "";

  const dias = Math.round((fim.getTime() - inicio.getTime()) / 86400000);
  if (!Number.isFinite(dias) || dias < 0) return "";
  return `${dias} dias`;
}

function nomeArquivoSeguro(nome = "laudo") {
  return String(nome)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "-");
}

export default function CadastroModuloElasticidade() {
  const history = useHistory();
  const [usuario, setUsuario] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obrasTodas, setObrasTodas] = useState([]);
  const [obrasSistema, setObrasSistema] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [moduloEditando, setModuloEditando] = useState(null);
  const [form, setForm] = useState(estadoInicial);
  const [arquivosLaudo, setArquivosLaudo] = useState([null, null]);
  const [filtro, setFiltro] = useState("");
  const [modoPrint, setModoPrint] = useState(false);
  const [moduloPrint, setModuloPrint] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const [itensPerPage, setItensPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [amostrasModuloElasticidade, setAmostrasModuloElasticidade] = useState([]);
  const [carregandoAmostras, setCarregandoAmostras] = useState(false);
  const [certificadosEquipamentos, setCertificadosEquipamentos] = useState([]);
  const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: "", tipo: "error" });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUsuario(null);
        return;
      }

      const snap = await getDoc(doc(db, "users", user.uid));
      setUsuario(snap.exists() ? { uid: user.uid, ...snap.data() } : { uid: user.uid });
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    carregarModulos();
    carregarClientes();
    carregarObras();
    carregarCertificadosEquipamentos();
  }, []);

  async function carregarModulos() {
    setLoading(true);
    const q = query(collection(db, "tblModulosElasticidade"), orderBy("criadoEm", "desc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setModulos(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
    setLoading(false);
  }

  async function carregarClientes() {
    const q = query(collection(db, "clientes"), orderBy("nome", "asc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setClientes(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
  }

  async function carregarObras() {
    const q = query(collection(db, "obras"), orderBy("nome", "asc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setObrasTodas(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
  }

  async function carregarCertificadosEquipamentos() {
    const [equipamentosSnap, documentosSnap] = await Promise.all([
      getDocs(consultaTenantAtual(collection(db, "equipamentos"))),
      getDocs(consultaTenantAtual(collection(db, "eq_documentos"))),
    ]);

    const documentos = documentosSnap.docs.map((documento) => ({
      id: documento.id,
      ...documento.data(),
    }));

    const equipamentosComCertificado = await Promise.all(
      equipamentosSnap.docs.map(async (documento) => {
        const equipamento = {
          id: documento.id,
          ...documento.data(),
        };

        const certificado = documentos.find(
          (item) =>
            item.equipamentoId === equipamento.id &&
            item.statusDocumento === "ENVIADO" &&
            (
              item.tipoDocumentoCodigo === "CERTIFICADO" ||
              String(item.tipoDocumentoNome || "").toUpperCase().includes("CERTIFICADO")
            )
        );

        let url = "";

        if (certificado?.storagePathAtual) {
          try {
            url = await getDownloadURL(ref(storage, certificado.storagePathAtual));
          } catch {
            url = "";
          }
        }

        return {
          id: equipamento.id,
          equipamentoId: equipamento.id,
          equipamentoNome: equipamento.nome || "",
          equipamentoDescricao: equipamento.descricao || "",
          certificadoId: certificado?.id || "",
          numeroCertificado: certificado?.numeroCertificado || "",
          nomeArquivoOriginal: certificado?.nomeArquivoOriginal || "",
          storagePathAtual: certificado?.storagePathAtual || "",
          url,
        };
      })
    );

    setCertificadosEquipamentos(
      equipamentosComCertificado
        .filter((item) => item.certificadoId)
        .sort((a, b) =>
          String(a.equipamentoNome || "").localeCompare(
            String(b.equipamentoNome || ""),
            "pt-BR"
          )
        )
    );
  }

  async function carregarObrasPorCliente(clienteId) {
    if (!clienteId) {
      setObrasSistema([]);
      return;
    }

    const q = query(collection(db, "obras"), where("clienteId", "==", clienteId), orderBy("nome", "asc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setObrasSistema(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
  }

  function abrirNovoCadastro() {
    setModoEdicao(false);
    setModuloEditando(null);
    setClienteSelecionado(null);
    setObrasSistema([]);
    setArquivosLaudo([null, null]);
    setAmostrasModuloElasticidade([]);
    setForm({
      ...estadoInicial,
    });
    setOpenDialog(true);
  }

  async function editarModulo(modulo) {
    setModoEdicao(true);
    setModuloEditando(modulo);
    setArquivosLaudo([null, null]);
    setAmostrasModuloElasticidade([]);

    const clienteEncontrado = clientes.find((cliente) => cliente.id === modulo.clienteId);
    setClienteSelecionado(clienteEncontrado || null);

    if (modulo.clienteId) {
      await carregarObrasPorCliente(modulo.clienteId);
    }

    setForm({
      ...estadoInicial,
      ...modulo,
      laudosEnsaio: Array.isArray(modulo.laudosEnsaio) ? modulo.laudosEnsaio : [],
    });
    await carregarAmostrasModuloElasticidade(modulo.dataMoldagem, modulo.clienteId);
    setOpenDialog(true);
  }

  async function criarNovaIdade(modulo) {
    const hoje = new Date().toISOString().split("T")[0];
    setModoEdicao(false);
    setModuloEditando(null);
    setArquivosLaudo([null, null]);
    setAmostrasModuloElasticidade([]);

    const clienteEncontrado = clientes.find((cliente) => cliente.id === modulo.clienteId);
    setClienteSelecionado(clienteEncontrado || null);

    if (modulo.clienteId) {
      await carregarObrasPorCliente(modulo.clienteId);
    }

    const dadosOriginais = { ...modulo };
    ["id", "tenantId", "criadoEm", "criadoPor", "criadoPorEmail", "atualizadoEm", "atualizadoPor", "atualizadoPorEmail"]
      .forEach((campo) => delete dadosOriginais[campo]);

    setForm({
      ...estadoInicial,
      ...dadosOriginais,
      codigo: "",
      dataRelatorio: hoje,
      dataEnsaio: hoje,
      idadeCp: diferencaDias(modulo.dataMoldagem, hoje),
      moduloEciCp1: "",
      moduloEciCp2: "",
      moduloDinamicoCp1: "",
      moduloDinamicoCp2: "",
      laudosEnsaio: [],
    });

    await carregarAmostrasModuloElasticidade(modulo.dataMoldagem, modulo.clienteId);
    setOpenDialog(true);
  }

  async function selecionarObra(obraId) {
    if (!obraId) {
      setClienteSelecionado(null);
      setForm((atual) => ({
        ...atual,
        cliente: "",
        clienteId: "",
        obra: "",
        obraId: "",
      }));
      return;
    }

    const obraSelecionada = [...obrasTodas, ...obrasSistema].find((obra) => obra.id === obraId);
    const clienteEncontrado = clientes.find((cliente) => cliente.id === obraSelecionada?.clienteId);

    if (clienteEncontrado) {
      setClienteSelecionado(clienteEncontrado);
    }

    const clienteIdSelecionado = clienteEncontrado?.id || obraSelecionada?.clienteId || "";

    setForm((atual) => ({
      ...atual,
      cliente: clienteEncontrado?.nome || obraSelecionada?.clienteNome || obraSelecionada?.cliente || atual.cliente,
      clienteId: clienteIdSelecionado || atual.clienteId,
      obra: obraSelecionada?.nome || "",
      obraId: obraSelecionada?.id || "",
    }));

    await carregarAmostrasModuloElasticidade(form.dataMoldagem, clienteIdSelecionado);
  }

  async function buscarAmostraNaAgenda(
    dataMoldagemAtual = form.dataMoldagem,
    notaAtual = form.notaFiscalRomaneio,
    agendaRupturaIdAtual = form.agendaRupturaId,
    caminhaoIdAtual = form.caminhaoId
  ) {
    if (!dataMoldagemAtual || !String(notaAtual || "").trim()) return;

    const q = query(collection(db, "tblAgendaRuptura"), where("datamoldagem", "==", dataMoldagemAtual));
    const snap = await getDocs(consultaTenantAtual(q));
    const notaNormalizada = normalizarTexto(notaAtual).trim();

    const moldagensOrdenadas = [...snap.docs].sort((a, b) => {
      if (a.id === agendaRupturaIdAtual) return -1;
      if (b.id === agendaRupturaIdAtual) return 1;
      return 0;
    });

    for (const moldagemDoc of moldagensOrdenadas) {
      const moldagem = { id: moldagemDoc.id, ...moldagemDoc.data() };
      const caminhoesSnap = await getDocs(collection(db, "tblAgendaRuptura", moldagemDoc.id, "caminhoes"));
      const caminhaoDoc = caminhoesSnap.docs.find((item) => {
        const caminhao = item.data();
        if (caminhao.status === "cancelado") return false;
        if (caminhao.moduloElasticidade !== true) return false;
        if (
          agendaRupturaIdAtual === moldagem.id &&
          caminhaoIdAtual &&
          item.id !== caminhaoIdAtual
        ) {
          return false;
        }
        return normalizarTexto(caminhao.notaFiscalRomaneio).trim() === notaNormalizada;
      });

      if (caminhaoDoc) {
        const caminhao = { id: caminhaoDoc.id, ...caminhaoDoc.data() };
        const cliente = clientes.find((item) => item.id === moldagem.clienteId) || null;

        if (moldagem.clienteId) {
          await carregarObrasPorCliente(moldagem.clienteId);
        }

        setClienteSelecionado(cliente);
        setForm((atual) => ({
          ...atual,
          cliente: moldagem.cliente || cliente?.nome || "",
          clienteId: moldagem.clienteId || "",
          obra: moldagem.obra || "",
          obraId: moldagem.obraId || "",
          dataMoldagem: moldagem.datamoldagem || dataMoldagemAtual,
          notaFiscalRomaneio: caminhao.notaFiscalRomaneio || notaAtual,
          agendaRupturaId: moldagem.id,
          caminhaoId: caminhao.id,
          concreteira: moldagem.concreteira || "",
          localAplicacao: concatenarLocalAplicacao(caminhao.observacoes, caminhao.pecaConcretada),
          fckProjeto: moldagem.fckProjeto || "",
          abatimento: caminhao.slumpObtido || "",
          tipoConcreto: formatarTipoLancamento(moldagem.tipoLancamento),
          idadeCp: diferencaDias(moldagem.datamoldagem || dataMoldagemAtual, atual.dataEnsaio),
        }));
        return;
      }
    }

    alert("Nenhuma amostra encontrada na agenda de ruptura para esta data de moldagem e nota/romaneio.");
  }

  async function carregarAmostrasModuloElasticidade(dataMoldagemAtual, clienteIdAtual = form.clienteId) {
    setAmostrasModuloElasticidade([]);

    if (!dataMoldagemAtual && !clienteIdAtual) return;

    try {
      setCarregandoAmostras(true);

      const filtros = [];
      if (clienteIdAtual) {
        filtros.push(where("clienteId", "==", clienteIdAtual));
      } else if (dataMoldagemAtual) {
        filtros.push(where("datamoldagem", "==", dataMoldagemAtual));
      }

      const moldagensSnap = await getDocs(
        consultaTenantAtual(query(
          collection(db, "tblAgendaRuptura"),
          ...filtros
        ))
      );

      const listas = await Promise.all(
        moldagensSnap.docs.map(async (moldagemDoc) => {
          const moldagem = { id: moldagemDoc.id, ...moldagemDoc.data() };
          if (dataMoldagemAtual && moldagem.datamoldagem !== dataMoldagemAtual) {
            return [];
          }

          const caminhoesSnap = await getDocs(
            collection(db, "tblAgendaRuptura", moldagemDoc.id, "caminhoes")
          );

          return caminhoesSnap.docs
            .map((caminhaoDoc) => ({
              id: caminhaoDoc.id,
              ...caminhaoDoc.data(),
              agendaRupturaId: moldagem.id,
              obra: moldagem.obra || "",
              cliente: moldagem.cliente || "",
              dataMoldagem: moldagem.datamoldagem || "",
            }))
            .filter(
              (caminhao) =>
                caminhao.status !== "cancelado" &&
                caminhao.moduloElasticidade === true &&
                String(caminhao.notaFiscalRomaneio || "").trim() !== ""
            );
        })
      );

      const amostras = listas
        .flat()
        .sort((a, b) =>
          String(a.notaFiscalRomaneio || "").localeCompare(
            String(b.notaFiscalRomaneio || ""),
            "pt-BR",
            { numeric: true }
          )
        );

      setAmostrasModuloElasticidade(amostras);
    } finally {
      setCarregandoAmostras(false);
    }
  }

  async function uploadLaudos(moduloId, laudosAtuais = []) {
    const laudos = [...(Array.isArray(laudosAtuais) ? laudosAtuais : [])];

    for (let index = 0; index < arquivosLaudo.length; index += 1) {
      const arquivo = arquivosLaudo[index];
      if (!arquivo) continue;

      const caminho = `modulos-elasticidade/${moduloId}/laudos/${index + 1}-${Date.now()}-${nomeArquivoSeguro(arquivo.name)}`;
      const storageRef = ref(storage, caminho);
      const contentType = arquivo.type || (/\.pdf$/i.test(arquivo.name) ? "application/pdf" : "image/jpeg");
      await uploadBytes(storageRef, arquivo, { contentType });
      const url = await getDownloadURL(storageRef);
      laudos[index] = {
        nome: arquivo.name,
        path: caminho,
        url,
        tipo: contentType,
      };
    }

    return laudos.filter(Boolean).slice(0, 2);
  }

  async function salvarModulo() {
    if (!form.clienteId || !form.obraId) {
      alert("Selecione a empresa e a obra antes de salvar.");
      return;
    }

    if (!form.dataMoldagem || !form.notaFiscalRomaneio) {
      alert("Informe a data de moldagem e a nota fiscal/romaneio da amostra.");
      return;
    }

    if (
      eciInvalido(form.moduloEciCp1, form.moduloDinamicoCp1) ||
      eciInvalido(form.moduloEciCp2, form.moduloDinamicoCp2)
    ) {
      alert("O Eci deve ser sempre menor que o módulo dinâmico.");
      return;
    }

    try {
      setSalvando(true);

      const formParaSalvar = { ...form };
      delete formParaSalvar.dataCadastro;
      delete formParaSalvar.tipoCapeamento;
      const codigo = modoEdicao
        ? formParaSalvar.codigo
        : formParaSalvar.codigo || (await gerarSequencial({
            chave: "modulo_elasticidade",
            escopo: new Date(formParaSalvar.dataRelatorio || new Date()).getFullYear(),
            formato: {
              padding: 3,
              ano: new Date(formParaSalvar.dataRelatorio || new Date()).getFullYear(),
              template: "CE-RE-MED-{numero}/{ano}",
            },
            valorMinimo: obterMaiorSequencial(modulos, /CE-RE-MED-(\d+)\/\d{4}/),
          })).codigo;
      const dadosBase = {
        ...formParaSalvar,
        codigo,
        idadeCp: form.idadeCp || diferencaDias(form.dataMoldagem, form.dataEnsaio),
        atualizadoEm: new Date(),
        atualizadoPor: usuario?.uid || null,
        atualizadoPorEmail: usuario?.email || null,
      };

      if (modoEdicao && moduloEditando?.id) {
        const laudosEnsaio = await uploadLaudos(moduloEditando.id, form.laudosEnsaio);
        await updateDoc(doc(db, "tblModulosElasticidade", moduloEditando.id), {
          ...dadosBase,
          laudosEnsaio,
        });
      } else {
        const docRef = await addDoc(collection(db, "tblModulosElasticidade"), aplicarTenantAtual({
          ...dadosBase,
          laudosEnsaio: [],
          criadoEm: new Date(),
          criadoPor: usuario?.uid || null,
          criadoPorEmail: usuario?.email || null,
        }));
        const laudosEnsaio = await uploadLaudos(docRef.id, []);
        if (laudosEnsaio.length) {
          await updateDoc(doc(db, "tblModulosElasticidade", docRef.id), { laudosEnsaio });
        }
      }

      setOpenDialog(false);
      await carregarModulos();
    } catch (error) {
      console.error("Erro ao salvar módulo de elasticidade:", error);
      const codigoErro = String(error?.code || "").toLowerCase();
      const falhaComunicacao = [
        "deadline-exceeded",
        "unavailable",
        "network-request-failed",
        "retry-limit-exceeded",
      ].some((codigo) => codigoErro.includes(codigo));

      setNotificacao({
        aberta: true,
        tipo: "error",
        mensagem: falhaComunicacao
          ? "Falha de comunicação com o servidor. Não foi possível confirmar a conclusão do salvamento. Confira a lista antes de tentar novamente para evitar um registro duplicado."
          : "Não foi possível concluir o salvamento do módulo de elasticidade. Tente novamente.",
      });
    } finally {
      setSalvando(false);
    }
  }

  async function abrirRelatorio(modulo, imprimir = false) {
    let moduloAtualizado = modulo;

    if (modulo?.id) {
      const snap = await getDoc(doc(db, "tblModulosElasticidade", modulo.id));
      if (snap.exists()) {
        moduloAtualizado = { id: snap.id, ...snap.data() };
      }
    }

    setModuloPrint(moduloAtualizado);
    setAutoPrint(imprimir);
    setModoPrint(true);
  }

  const modulosFiltrados = useMemo(() => {
    const termo = normalizarTexto(filtro);
    const listaOrdenada = [...modulos].sort((a, b) => {
      const diferencaCodigo = numeroCodigoModulo(b.codigo) - numeroCodigoModulo(a.codigo);
      if (diferencaCodigo !== 0) return diferencaCodigo;
      return String(b.codigo || "").localeCompare(String(a.codigo || ""), "pt-BR", { numeric: true });
    });

    if (!termo) return listaOrdenada;

    return listaOrdenada.filter((item) =>
      [item.codigo, item.cliente, item.obra, item.dataEnsaio, item.dataMoldagem, item.notaFiscalRomaneio].some((campo) =>
        contemTextoPesquisa(normalizarTexto(campo), termo)
      )
    );
  }, [modulos, filtro]);

  const inicio = currentPage * itensPerPage;
  const fim = inicio + itensPerPage;
  const modulosPaginados = modulosFiltrados.slice(inicio, fim);

  if (modoPrint && moduloPrint) {
    return (
      <RelatorioModuloElasticidadePrint
        modulo={moduloPrint}
        autoPrint={autoPrint}
        onVoltar={() => {
          setModoPrint(false);
          setAutoPrint(false);
          setModuloPrint(null);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Módulo de Elasticidade</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Cadastro de resultados do ensaio</Typography>
        <Stack direction={{ xs: "column", md: "row" }} alignItems="stretch" spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirNovoCadastro}>
            + Novo Módulo
          </Button>

          <Button variant="outlined" onClick={() => history.push("/app/relatorios/modulo-elasticidade")}>
            Gerar relatório
          </Button>

          <TextField
            size="small"
            label="Pesquisar"
            placeholder="Código, empresa, obra, data ou nota"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setCurrentPage(0);
            }}
            sx={{ flex: 1 }}
          />

          <TextField
            size="small"
            label="Qtd Módulo"
            value={modulosFiltrados.length}
            InputProps={{ readOnly: true }}
            sx={{ width: { xs: "100%", md: 140 } }}
          />
        </Stack>
        </Paper>

        <Paper
          sx={{
            borderRadius: "12px",
            overflow: "auto",
            border: "1px solid #e2e8f0",
            boxShadow: "none",
          }}
        >
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Código</strong></TableCell>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Data Ensaio</TableCell>
                <TableCell>Nota/Romaneio</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modulosPaginados.map((item) => (
                <TableRow key={item.id} hover sx={{ height: 48 }}>
                  <TableCell>{item.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="lista-dado-principal">{item.cliente || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obra || "-"}</div>
                  </TableCell>
                <TableCell className="lista-dado-secundario">{formatarDataBR(item.dataEnsaio || item.dataRelatorio)}</TableCell>
                  <TableCell className="lista-dado-destaque">{item.notaFiscalRomaneio || "-"}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar relatório">
                      <IconButton size="small" onClick={() => abrirRelatorio(item, false)}>
                        <VisibilityIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar cadastro">
                      <IconButton size="small" onClick={() => editarModulo(item)}>
                        <EditIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Nova idade">
                      <IconButton size="small" onClick={() => criarNovaIdade(item)}>
                        <AddCircleOutlineIcon sx={{ color: "#2e7d32" }} />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro
                      colecao="tblModulosElasticidade"
                      documentoId={item.id}
                      registro={item}
                      resolverEmailPorUid
                    />
                  </TableCell>
                </TableRow>
              ))}

              {loading && <TableRow><TableCell colSpan={5}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!loading && modulosPaginados.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum módulo cadastrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Stack direction="row" justifyContent="space-between" mt={2}>
          <TextField
            select
            size="small"
            label="Itens"
            value={itensPerPage}
            onChange={(e) => {
              setItensPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            sx={{ width: 120 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </TextField>

          <Stack direction="row" spacing={1}>
            <Button disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>
              Anterior
            </Button>
            <Typography sx={{ display: "flex", alignItems: "center" }}>
              Página {currentPage + 1}
            </Typography>
            <Button disabled={fim >= modulosFiltrados.length} onClick={() => setCurrentPage((p) => p + 1)}>
              Próxima
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{modoEdicao ? "Editar Módulo" : "Novo Módulo"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Código" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} fullWidth />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Data do relatório"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.dataRelatorio}
                onChange={(e) => setForm({ ...form, dataRelatorio: e.target.value })}
                fullWidth
              />
              <TextField
                label="Data do ensaio"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.dataEnsaio}
                onChange={(e) =>
                  setForm({
                    ...form,
                    dataEnsaio: e.target.value,
                    idadeCp: diferencaDias(form.dataMoldagem, e.target.value),
                  })
                }
                fullWidth
              />
            </Stack>

            <Autocomplete
              options={obrasTodas}
              getOptionLabel={(option) => option?.nome || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={obrasTodas.find((obra) => obra.id === form.obraId) || null}
              onChange={(event, newValue) => selecionarObra(newValue?.id || "")}
              renderInput={(params) => <TextField {...params} label="Obra" placeholder="Pesquisar obra..." fullWidth />}
            />

            <TextField
              label="Empresa"
              value={form.cliente || clienteSelecionado?.nome || ""}
              InputProps={{ readOnly: true }}
              fullWidth
            />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Data de moldagem"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.dataMoldagem}
                onChange={(e) => {
                  const dataMoldagem = e.target.value;
                  setForm({
                    ...form,
                    dataMoldagem,
                    notaFiscalRomaneio: "",
                    agendaRupturaId: "",
                    caminhaoId: "",
                    idadeCp: diferencaDias(dataMoldagem, form.dataEnsaio),
                  });
                  carregarAmostrasModuloElasticidade(dataMoldagem, form.clienteId);
                }}
                sx={{ width: { xs: "100%", md: 220 }, flexShrink: 0 }}
              />
              <Autocomplete
                freeSolo
                options={amostrasModuloElasticidade}
                loading={carregandoAmostras}
                sx={{ flex: 1, minWidth: 0 }}
                value={
                  amostrasModuloElasticidade.find(
                    (amostra) =>
                      amostra.id === form.caminhaoId &&
                      amostra.agendaRupturaId === form.agendaRupturaId
                  ) || null
                }
                inputValue={form.notaFiscalRomaneio || ""}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : String(option?.notaFiscalRomaneio || "")
                }
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id &&
                  option.agendaRupturaId === value.agendaRupturaId
                }
                onInputChange={(event, novoValor, reason) => {
                  if (reason === "reset") return;
                  setForm((atual) => ({
                    ...atual,
                    notaFiscalRomaneio: novoValor,
                    agendaRupturaId: "",
                    caminhaoId: "",
                  }));
                }}
                onChange={async (event, novaAmostra) => {
                  if (typeof novaAmostra === "string") {
                    setForm((atual) => ({
                      ...atual,
                      notaFiscalRomaneio: novaAmostra,
                      agendaRupturaId: "",
                      caminhaoId: "",
                    }));
                    return;
                  }

                  const notaFiscalRomaneio =
                    novaAmostra?.notaFiscalRomaneio || "";

                  setForm((atual) => ({
                    ...atual,
                    dataMoldagem: novaAmostra?.dataMoldagem || atual.dataMoldagem,
                    notaFiscalRomaneio,
                    agendaRupturaId: novaAmostra?.agendaRupturaId || "",
                    caminhaoId: novaAmostra?.id || "",
                  }));

                  if (novaAmostra) {
                    await buscarAmostraNaAgenda(
                      novaAmostra.dataMoldagem || form.dataMoldagem,
                      notaFiscalRomaneio,
                      novaAmostra.agendaRupturaId,
                      novaAmostra.id
                    );
                  }
                }}
                noOptionsText={
                  form.clienteId || form.dataMoldagem
                    ? "Nenhum romaneio marcado como ME encontrado"
                    : "Selecione uma obra/empresa para pesquisar, ou digite manualmente"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nota fiscal/romaneio (ME)"
                    placeholder="Pesquisar por cliente ou digitar manualmente"
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Concreteira" value={form.concreteira} onChange={(e) => setForm({ ...form, concreteira: e.target.value })} fullWidth />
              <TextField label="Local de aplicação" value={form.localAplicacao} onChange={(e) => setForm({ ...form, localAplicacao: e.target.value })} fullWidth />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="FCK de projeto (MPa)" value={form.fckProjeto} onChange={(e) => setForm({ ...form, fckProjeto: e.target.value })} fullWidth />
              <TextField label="Abatimento (mm)" value={form.abatimento} onChange={(e) => setForm({ ...form, abatimento: e.target.value })} fullWidth />
              <TextField label="Idade do CP" value={form.idadeCp || diferencaDias(form.dataMoldagem, form.dataEnsaio)} InputProps={{ readOnly: true }} fullWidth />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Tipo de concreto" value={form.tipoConcreto} onChange={(e) => setForm({ ...form, tipoConcreto: e.target.value })} fullWidth />
              <TextField label="Tipo de cura" value={form.tipoCura} onChange={(e) => setForm({ ...form, tipoCura: e.target.value })} fullWidth />
            </Stack>

            <Typography fontWeight={700}>Dimensões e resultados</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Massa CP1 (kg)" value={form.massaCp1} onChange={(e) => setForm({ ...form, massaCp1: e.target.value })} fullWidth />
              <TextField label="Altura CP1 (mm)" value={form.alturaCp1} onChange={(e) => setForm({ ...form, alturaCp1: e.target.value })} fullWidth />
              <TextField label="Diâmetro CP1 (mm)" value={form.diametroCp1} onChange={(e) => setForm({ ...form, diametroCp1: e.target.value })} fullWidth />
              <TextField label="Eci CP1 (GPa)" value={form.moduloEciCp1} onChange={(e) => setForm({ ...form, moduloEciCp1: e.target.value })} onBlur={() => setForm((atual) => ({ ...atual, moduloEciCp1: formatarDecimalDuasCasas(atual.moduloEciCp1) }))} error={eciInvalidoAoDigitar(form.moduloEciCp1, form.moduloDinamicoCp1)} helperText={eciInvalidoAoDigitar(form.moduloEciCp1, form.moduloDinamicoCp1) ? "O Eci deve ser sempre menor que o módulo dinâmico." : ""} fullWidth />
              <TextField label="Dinâmico CP1 (GPa)" value={form.moduloDinamicoCp1} onChange={(e) => setForm({ ...form, moduloDinamicoCp1: e.target.value })} onBlur={() => setForm((atual) => ({ ...atual, moduloDinamicoCp1: formatarDecimalDuasCasas(atual.moduloDinamicoCp1) }))} fullWidth />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Massa CP2 (kg)" value={form.massaCp2} onChange={(e) => setForm({ ...form, massaCp2: e.target.value })} fullWidth />
              <TextField label="Altura CP2 (mm)" value={form.alturaCp2} onChange={(e) => setForm({ ...form, alturaCp2: e.target.value })} fullWidth />
              <TextField label="Diâmetro CP2 (mm)" value={form.diametroCp2} onChange={(e) => setForm({ ...form, diametroCp2: e.target.value })} fullWidth />
              <TextField label="Eci CP2 (GPa)" value={form.moduloEciCp2} onChange={(e) => setForm({ ...form, moduloEciCp2: e.target.value })} onBlur={() => setForm((atual) => ({ ...atual, moduloEciCp2: formatarDecimalDuasCasas(atual.moduloEciCp2) }))} error={eciInvalidoAoDigitar(form.moduloEciCp2, form.moduloDinamicoCp2)} helperText={eciInvalidoAoDigitar(form.moduloEciCp2, form.moduloDinamicoCp2) ? "O Eci deve ser sempre menor que o módulo dinâmico." : ""} fullWidth />
              <TextField label="Dinâmico CP2 (GPa)" value={form.moduloDinamicoCp2} onChange={(e) => setForm({ ...form, moduloDinamicoCp2: e.target.value })} onBlur={() => setForm((atual) => ({ ...atual, moduloDinamicoCp2: formatarDecimalDuasCasas(atual.moduloDinamicoCp2) }))} fullWidth />
            </Stack>

            <Autocomplete
              options={certificadosEquipamentos}
              value={
                certificadosEquipamentos.find(
                  (item) => item.certificadoId === form.certificadoAparelho?.certificadoId
                ) || null
              }
              getOptionLabel={(option) =>
                option
                  ? [
                      option.equipamentoNome || "Equipamento",
                      option.numeroCertificado
                        ? `Certificado ${option.numeroCertificado}`
                        : option.nomeArquivoOriginal || "Certificado cadastrado",
                      option.url ? "" : "arquivo não carregado",
                    ]
                      .filter(Boolean)
                      .join(" - ")
                  : ""
              }
              isOptionEqualToValue={(option, value) => option.certificadoId === value.certificadoId}
              onChange={(event, value) => setForm({ ...form, certificadoAparelho: value || null })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Certificado do aparelho utilizado"
                  placeholder="Selecione o equipamento/certificado do ensaio"
                  fullWidth
                />
              )}
            />

            <Typography fontWeight={700}>Laudo do ensaio</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                type="file"
                label="Laudo CP1"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "image/*,application/pdf" }}
                onChange={(e) => setArquivosLaudo([e.target.files?.[0] || null, arquivosLaudo[1]])}
                helperText={form.laudosEnsaio?.[0]?.nome || ""}
                fullWidth
              />
              <TextField
                type="file"
                label="Laudo CP2"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "image/*,application/pdf" }}
                onChange={(e) => setArquivosLaudo([arquivosLaudo[0], e.target.files?.[0] || null])}
                helperText={form.laudosEnsaio?.[1]?.nome || ""}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" disabled={salvando} onClick={salvarModulo}>
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notificacao.aberta}
        autoHideDuration={9000}
        onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notificacao.tipo}
          variant="filled"
          onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
          sx={{ width: "100%" }}
        >
          {notificacao.mensagem}
        </Alert>
      </Snackbar>
    </>
  );
}
